import React, {
    createContext,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import config from './config';

const RouterContext = createContext(null);

// ── Language-prefixed URLs ────────────────────────────────────────────────
// Every page lives under a language segment (/en/…, /de/…). These helpers are
// the single place that adds or removes that prefix; the URL itself is the
// source of truth for the active language.
export const SUPPORTED_LANGS = config.languages;
export const DEFAULT_LANG = config.defaultLanguage;

const stripQueryHash = (value = '') => value.split('#')[0].split('?')[0];

// Returns the language code if pathname starts with a supported one, else null.
export const getLangFromPath = (pathname = '/') => {
    const [segment] = stripQueryHash(pathname).split('/').filter(Boolean);
    return SUPPORTED_LANGS.includes(segment) ? segment : null;
};

// Removes the leading language segment, returning the logical path the routes
// are defined against ('/en/about' → '/about', '/de' → '/').
export const stripLang = (pathname = '/') => {
    const lang = getLangFromPath(pathname);
    if (!lang) {
        return pathname || '/';
    }
    const rest = pathname.slice(lang.length + 1); // drop "/<lang>"
    return rest.startsWith('/') ? rest : `/${rest}`;
};

// Prefixes a same-origin absolute path with a language segment, preserving any
// query/hash. Leaves external URLs, relative paths, and already-prefixed paths
// untouched (idempotent).
export const localizeHref = (to, lang) => {
    if (typeof to !== 'string' || !to.startsWith('/')) {
        return to;
    }
    if (getLangFromPath(to)) {
        return to;
    }
    return to === '/' ? `/${lang}` : `/${lang}${to}`;
};

const getWindowLocation = () => ({
    pathname: window.location.pathname || '/',
    search: window.location.search || '',
    hash: window.location.hash || '',
    state: window.history.state?.usr || null,
});

const normalizeSearch = (search = '') => {
    if (!search) {
        return '';
    }

    return search.startsWith('?') ? search : `?${search}`;
};

const normalizeHash = (hash = '') => {
    if (!hash) {
        return '';
    }

    return hash.startsWith('#') ? hash : `#${hash}`;
};

const createHref = (to) => {
    if (typeof to === 'string') {
        return to;
    }

    const pathname = to.pathname || window.location.pathname || '/';
    return `${pathname}${normalizeSearch(to.search)}${normalizeHash(to.hash)}`;
};

const isModifiedEvent = (event) => event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

const isSameOrigin = (href) => {
    try {
        return new URL(href, window.location.href).origin === window.location.origin;
    } catch (error) {
        return false;
    }
};

export const RouterProvider = ({ children }) => {
    const [location, setLocation] = useState(getWindowLocation);

    useEffect(() => {
        const handlePopState = () => setLocation(getWindowLocation());

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const navigate = useCallback((to, options = {}) => {
        const href = createHref(to);
        const url = new URL(href, window.location.href);

        if (url.origin !== window.location.origin) {
            window.location.assign(url.href);
            return;
        }

        const nextPath = `${url.pathname}${url.search}${url.hash}`;
        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const state = { usr: options.state || null };

        if (options.replace || nextPath === currentPath) {
            window.history.replaceState(state, '', nextPath);
        } else {
            window.history.pushState(state, '', nextPath);
        }

        setLocation(getWindowLocation());
    }, []);

    const value = useMemo(() => ({ location, navigate }), [location, navigate]);

    return (
        <RouterContext.Provider value={value}>
            {children}
        </RouterContext.Provider>
    );
};

const useRouter = () => {
    const router = useContext(RouterContext);

    if (!router) {
        throw new Error('Router hooks must be used inside RouterProvider.');
    }

    return router;
};

export const useLocation = () => useRouter().location;

export const useNavigate = () => useRouter().navigate;

export const Link = forwardRef(({
    to,
    replace = false,
    state = null,
    reloadDocument = false,
    onClick,
    target,
    children,
    ...props
}, ref) => {
    const { navigate, location } = useRouter();
    // Carry the active language onto every internal link automatically, so call
    // sites keep using logical paths like to="/about".
    const currentLang = getLangFromPath(location.pathname) || DEFAULT_LANG;
    const href = createHref(localizeHref(to, currentLang));

    const handleClick = (event) => {
        if (onClick) {
            onClick(event);
        }

        if (
            event.defaultPrevented ||
            reloadDocument ||
            event.button !== 0 ||
            isModifiedEvent(event) ||
            (target && target !== '_self') ||
            !isSameOrigin(href)
        ) {
            return;
        }

        event.preventDefault();
        navigate(href, { replace, state });
    };

    return (
        <a
            {...props}
            href={href}
            onClick={handleClick}
            ref={ref}
            target={target}
        >
            {children}
        </a>
    );
});

export const NavLink = forwardRef(({
    to,
    end = false,
    className,
    children,
    ...props
}, ref) => {
    const location = useLocation();
    const currentLang = getLangFromPath(location.pathname) || DEFAULT_LANG;
    const href = createHref(localizeHref(to, currentLang));
    const targetPath = new URL(href, window.location.href).pathname;
    const pathPrefix = targetPath.endsWith('/') ? targetPath : `${targetPath}/`;
    const isActive = end
        ? location.pathname === targetPath
        : location.pathname === targetPath || location.pathname.startsWith(pathPrefix);
    const resolvedClassName = typeof className === 'function'
        ? className({ isActive })
        : [className, isActive ? 'active' : null].filter(Boolean).join(' ');

    return (
        <Link
            {...props}
            to={to}
            ref={ref}
            className={resolvedClassName || undefined}
            aria-current={isActive ? 'page' : undefined}
        >
            {typeof children === 'function' ? children({ isActive }) : children}
        </Link>
    );
});

const trimSlashes = (value) => value.replace(/^\/+|\/+$/g, '');

export const matchPath = (pattern, pathname) => {
    if (!pattern) {
        return null;
    }

    if (pattern === '*') {
        return { params: {}, pathname };
    }

    const wildcard = pattern.endsWith('/*');
    const routeSegments = trimSlashes(wildcard ? pattern.slice(0, -2) : pattern).split('/').filter(Boolean);
    const pathSegments = trimSlashes(pathname).split('/').filter(Boolean);

    if ((!wildcard && routeSegments.length !== pathSegments.length) || (wildcard && pathSegments.length < routeSegments.length)) {
        return null;
    }

    const params = {};

    for (let index = 0; index < routeSegments.length; index += 1) {
        const routeSegment = routeSegments[index];
        const pathSegment = pathSegments[index];

        if (routeSegment.startsWith(':')) {
            params[routeSegment.slice(1)] = decodeURIComponent(pathSegment || '');
            continue;
        }

        if (routeSegment !== pathSegment) {
            return null;
        }
    }

    if (wildcard) {
        params['*'] = pathSegments.slice(routeSegments.length).join('/');
    }

    return { params, pathname };
};

export const matchRoute = (routes, pathname) => {
    for (const route of routes) {
        const match = matchPath(route.path, pathname);

        if (match) {
            return { route, match };
        }
    }

    return null;
};
